import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const FUNNEL_LABEL = { trial: "30 Day Trial", access: "Gym look-around", studio: "Studio hire", contact: "Contact", fuel: "Fuel Chapters" };

function Bars({ data, testId }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (!entries.length) return <p className="text-sm text-kore-ink/50">No leads yet.</p>;
  return (
    <ul className="space-y-3" data-testid={testId}>
      {entries.map(([k, v]) => (
        <li key={k} data-testid={`${testId}-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="flex justify-between text-xs text-kore-ink/70 mb-1"><span>{FUNNEL_LABEL[k] || k}</span><span className="font-medium text-kore-forest">{v}</span></div>
          <div className="h-2 rounded-full bg-kore-warm overflow-hidden"><div className="h-full bg-kore-forest rounded-full transition-[width] duration-500" style={{ width: `${(v / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

export default function LeadsOverview({ onOpenLead }) {
  const [report, setReport] = useState(null);
  const [due, setDue] = useState([]);

  useEffect(() => {
    api.get("/admin/leads/report").then((r) => setReport(r.data)).catch(() => {});
    api.get("/admin/leads/follow-ups").then((r) => setDue(r.data)).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="grid lg:grid-cols-3 gap-5 mb-8">
      <div data-testid="followups-panel" className={`rounded-2xl border p-6 ${due.length ? "bg-kore-gold/10 border-kore-gold/40" : "bg-white border-kore-gold/20"}`}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-kore-gold"><AlertCircle size={13} /> Follow-ups due</div>
        <div data-testid="followups-count" className="font-display text-4xl text-kore-forest mt-3">{due.length}</div>
        <ul className="mt-4 space-y-2 max-h-48 overflow-y-auto">
          {due.slice(0, 8).map((l) => (
            <li key={l.id} data-testid={`followup-${l.id}`}>
              <button onClick={() => onOpenLead?.(l)} className="w-full text-left text-sm flex justify-between gap-3 hover:text-kore-forest">
                <span className="truncate">{l.first_name} · <span className="text-kore-ink/55">{FUNNEL_LABEL[l.funnel] || l.funnel}</span></span>
                <span className={`text-xs shrink-0 ${l.follow_up_at < today ? "text-red-600" : "text-kore-ink/55"}`}>{l.follow_up_at < today ? "overdue" : "today"}</span>
              </button>
            </li>
          ))}
          {!due.length && <li className="text-sm text-kore-ink/50">Nothing due — you're on top of it.</li>}
        </ul>
      </div>
      <div data-testid="report-source" className="rounded-2xl bg-white border border-kore-gold/20 p-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-kore-gold">Leads by source</div>
        <div className="font-display text-4xl text-kore-forest mt-3">{report?.total ?? "—"}</div>
        <div className="mt-4"><Bars data={report?.by_source} testId="bars-source" /></div>
      </div>
      <div data-testid="report-funnel" className="rounded-2xl bg-white border border-kore-gold/20 p-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-kore-gold">Leads by funnel</div>
        <div className="mt-[3.25rem]"><Bars data={report?.by_funnel} testId="bars-funnel" /></div>
      </div>
    </div>
  );
}
